import React from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import {
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  IconButton,
  Typography,
  withStyles,
  FormControlLabel,
  Switch,
  Tooltip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ExpandMore from "@material-ui/icons/ExpandMore";
import { AlertDialogData } from "../../types";
import { LayoutConst } from "../../constants";
import {
  TreeDataType,
  StructureType,
  AddNode,
  ExistsNode,
  NewCategoryNodeName,
  NewFolderNodeName,
  ActionNameAdd,
} from "./model/TreeItemModel";
import { VirtualFolderTree } from "./VirtualFolderTree";
import { EditNodeInfoType, initialEditNodeInfo } from "./VirtualFolderTreeItem";

/** 展開パネル全体スタイル定義 */
const StyledExpansionPanel = withStyles({
  root: {
    border: "1px solid rgba(0, 0, 0, .125)",
    boxShadow: "none",
    height: "100%",
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      margin: "auto",
    },
  },
  expanded: {},
})(ExpansionPanel);

/** 展開パネルヘッダースタイル定義 */
const StyledExpansionPanelSummary = withStyles({
  root: {
    flexDirection: "row-reverse",
    backgroundColor: "rgba(0, 0, 0, .03)",
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    height: LayoutConst.expantionPanel.summary.height,
    minHeight: LayoutConst.expantionPanel.summary.height,
    "&$expanded": {
      height: LayoutConst.expantionPanel.summary.height,
      minHeight: LayoutConst.expantionPanel.summary.height,
    },
  },
  content: {
    "&$expanded": {
      margin: "12px 0",
    },
  },
  expanded: {},
  expandIcon: {
    transform: "rotate(270deg)",
    "&$expanded": {
      transform: "rotate(0deg)",
    },
  },
})(ExpansionPanelSummary);

/** 展開パネルボディスタイル定義 */
const StyledExpansionPanelDetails = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
    overflow: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      width: 10,
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: 10,
      boxShadow: "inset 0 0 6px rgba(0,0,0,0.0)",
      webkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.0)",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: 10,
      backgroundColor: "rgba(0,0,0,.1)",
    },
  },
}))(ExpansionPanelDetails);

/** スタイル定義 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    addButton: {
      position: "absolute",
      top: -5,
      right: 0,
    },
    switchDisplayAll: {
      position: "absolute",
      top: -2,
      right: 15,
    },
  })
);

interface Props {
  title: string;
  id: number;
  expanded: boolean;
  height: string;
  onChange?: (id: number, newExpanded: boolean) => void;
  children?: React.ReactNode;
  treeData: TreeDataType;
  getTreeDataById: (id: number | undefined) => TreeDataType | undefined;
  setTreeDataById: (
    id: number | undefined,
    structure: StructureType[] | undefined,
    expandedNode: string[] | undefined,
    isDisplayAll: boolean | undefined,
    update: boolean
  ) => void;
  handleClickNode: (
    event: React.MouseEvent<HTMLElement>,
    nodeId: string,
    configId: number,
    excludes?: string[]
  ) => void;
  setAlertDialogData: React.Dispatch<React.SetStateAction<AlertDialogData>>;
  isAdmin: boolean;
}

/** 仮想フォルダ―ツリーコンテナ */
export const VirtualFolderTreeContainer = (props: Props) => {
  const classes = useStyles();

  const {
    title,
    id,
    expanded,
    height,
    onChange,
    treeData,
    getTreeDataById,
    setTreeDataById,
    handleClickNode,
    setAlertDialogData,
    isAdmin
  } = props;

  const { expandedNode, isDisplayAll } = treeData;

  //------------------------------
  // [State]

  // 編集中ノード情報
  // selectedNodeId: クリック(右クリックも含む)で選択したnodeId(=path)
  // editNodeId: 編集中のnodeId(=path)
  // treeEditMode: コンテキストメニューで選択した処理
  const [editNodeInfo, setEditNodeInfo] = React.useState<EditNodeInfoType>(
    initialEditNodeInfo
  );

  //------------------------------
  // [Handler]

  /** ノード編集用のステートをクリア */
  const setRenderNodeStates = (updateRenderNodeId: string) => {
    setEditNodeInfo({
      selectedNodeId: "",
      editNodeId: "",
      treeEditMode: "",
      renderNodeId: updateRenderNodeId,
    });
  };

  /** パネルの展開 */
  const handleChange = (
    _event: React.ChangeEvent<{}>,
    newExpanded: boolean
  ) => {
    if (onChange) onChange(id, newExpanded);
  };

  /** ユーザー／ユーザーグループごとの全表示設定切替 */
  const handleChangeDisplayAll = (
    event: React.ChangeEvent<HTMLInputElement>,
    configId: number
  ) => {
    event.stopPropagation();
    const { checked } = event.target;
    setTreeDataById(configId, undefined, undefined, checked, false);
    // 編集用ノードのレンダー用の更新(全てのノードを更新)
    setRenderNodeStates("");
  };

  /** 新規フォルダ―(仮フォルダ―)作成 */
  const addTreeNode = (nodeId: string) => {
    const parentPath = nodeId;
    const dummyName =
      parentPath === "" ? NewCategoryNodeName : NewFolderNodeName;
    const dummyPath =
      parentPath === "" ? dummyName : `${parentPath}/${dummyName}`;
    const tree: TreeDataType | undefined = getTreeDataById(id);
    if (!tree) return;

    // 仮フォルダ―作成
    if (!ExistsNode(tree.structure, dummyPath)) {
      const addedNode = AddNode(tree.structure, parentPath, dummyName);
      const newExpandedNode = [...expandedNode, parentPath];
      setTreeDataById(id, addedNode, newExpandedNode, undefined, false);
    }

    // 編集ノードのステートセット
    setEditNodeInfo({
      selectedNodeId: parentPath,
      editNodeId: dummyPath,
      treeEditMode: ActionNameAdd,
      renderNodeId: parentPath,
    });
  };

  /** 新規カテゴリー作成(仮フォルダ―作成) */
  const handleAddCategory = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    addTreeNode("");
  };

  //------------------------------
  // コンポーネントの描画

  /** 全表示／チェックしたノードのみ表示の切替 */
  const switchDisplayAll = (
    <Switch
      checked={isDisplayAll}
      onChange={e => handleChangeDisplayAll(e, id)}
      onFocus={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      name="switchDisplayAll"
      size="small"
    />
  );

  /** ユーザー／ユーザーグループごとの仮想フォルダー設定 */
  const virtualFolderTree = (
    <VirtualFolderTree
      treeData={treeData}
      editNodeInfo={editNodeInfo}
      getTreeDataById={getTreeDataById}
      setTreeDataById={setTreeDataById}
      setEditNodeInfo={setEditNodeInfo}
      setRenderNodeStates={setRenderNodeStates}
      handleClickNode={handleClickNode}
      addTreeNode={addTreeNode}
      setAlertDialogData={setAlertDialogData}
      isAdmin={isAdmin}
    />
  );

  //--------------------
  // コンポーネント
  return (
    <StyledExpansionPanel square expanded={expanded} onChange={handleChange}>
      <StyledExpansionPanelSummary
        aria-label="Expand"
        aria-controls={`${id}-contents`}
        id={`${id}-header`}
        expandIcon={<ExpandMore />}
        IconButtonProps={{ edge: "start", size: "small", disableRipple: true }}
      >
        <Typography>{title}</Typography>
        <Tooltip title="全て表示" aria-label="switch-display-all">
          <FormControlLabel
            control={switchDisplayAll}
            className={classes.switchDisplayAll}
            label=""
          />
        </Tooltip>
        {
          isAdmin ?
            <Tooltip title="新しいカテゴリー作成" aria-label="add-category">
              <IconButton
                size="small"
                aria-label="Add category or keyword"
                className={classes.addButton}
                onClick={handleAddCategory}
                onFocus={e => e.stopPropagation()}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
            :
            <></>
        }
      </StyledExpansionPanelSummary>
      <StyledExpansionPanelDetails
        style={{
          height: `calc(${height} - ${LayoutConst.expantionPanel.summary.height}px`,
        }}
      >
        {treeData && virtualFolderTree}
      </StyledExpansionPanelDetails>
    </StyledExpansionPanel>
  );
};

React.memo(VirtualFolderTreeContainer);
